// Shared AI inline action logic for Tiptap editors.
// Used by both tiptap_editor_controller (single doc) and section_tiptap_controller (sectioned docs).
// Each caller passes its own editor instance and container element.

export function buildBubbleMenuElement() {
  const bubbleEl = document.createElement("div")
  bubbleEl.className = "tiptap-bubble-menu"

  const aiBtn = document.createElement("button")
  aiBtn.type = "button"
  aiBtn.className = "tiptap-toolbar-btn tiptap-ai-btn"
  aiBtn.title = "AI Actions"
  aiBtn.innerHTML = '<span class="fas fa-wand-magic-sparkles"></span>'

  const aiDropdown = document.createElement("div")
  aiDropdown.className = "tiptap-ai-dropdown"
  aiDropdown.style.display = "none"

  const actions = [
    { action: "rewrite", icon: "fa-pen", label: "Rewrite" },
    { action: "expand", icon: "fa-expand", label: "Expand" },
    { action: "simplify", icon: "fa-compress", label: "Simplify" },
    { action: "summarize", icon: "fa-align-left", label: "Summarize" },
  ]

  // Store references on the element for external access
  bubbleEl._aiDropdown = aiDropdown
  bubbleEl._actions = actions

  aiBtn.addEventListener("mousedown", (e) => {
    e.preventDefault()
    aiDropdown.style.display = aiDropdown.style.display !== "none" ? "none" : "flex"
  })
  bubbleEl.appendChild(aiBtn)
  bubbleEl.appendChild(aiDropdown)

  // Link button in bubble menu
  const linkEl = document.createElement("button")
  linkEl.type = "button"
  linkEl.className = "tiptap-toolbar-btn"
  linkEl.title = "Add Link (⌘K)"
  linkEl.dataset.bubbleCmd = "link"
  linkEl.innerHTML = '<span class="fas fa-link"></span>'
  bubbleEl.appendChild(linkEl)

  // Inline link input (hidden by default)
  const linkInput = document.createElement("div")
  linkInput.className = "tiptap-bubble-link-input"
  linkInput.style.display = "none"
  linkInput.innerHTML = `
    <input type="text" placeholder="Paste link..." class="bubble-link-field">
    <button type="button" class="bubble-link-apply"><span class="fas fa-check"></span></button>
  `
  bubbleEl.appendChild(linkInput)
  bubbleEl._linkInput = linkInput

  document.body.appendChild(bubbleEl)
  return bubbleEl
}

// Wire up the action buttons to call triggerAiAction on the active context
export function wireAiDropdown(bubbleEl, getContext) {
  const dropdown = bubbleEl._aiDropdown
  const actions = bubbleEl._actions
  // Remove old buttons
  dropdown.innerHTML = ""
  actions.forEach(a => {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.innerHTML = `<span class="fas ${a.icon} mr-2"></span>${a.label}`
    btn.addEventListener("mousedown", (e) => {
      e.preventDefault()
      e.stopPropagation()
      dropdown.style.display = "none"
      const ctx = getContext()
      if (ctx) triggerAiAction(ctx.editor, a.action, ctx)
    })
    dropdown.appendChild(btn)
  })

  // Wire link button
  const linkBtn = bubbleEl.querySelector("[data-bubble-cmd='link']")
  const linkInput = bubbleEl._linkInput
  if (linkBtn && linkInput) {
    const field = linkInput.querySelector(".bubble-link-field")
    const apply = linkInput.querySelector(".bubble-link-apply")

    linkBtn.addEventListener("mousedown", (e) => {
      e.preventDefault()
      e.stopPropagation()
      const ctx = getContext()
      if (!ctx || !ctx.editor) return
      field.value = ctx.editor.getAttributes("link").href || ""
      linkInput.style.display = "flex"
      setTimeout(() => field.focus(), 10)
    })

    const applyLink = () => {
      const ctx = getContext()
      if (!ctx || !ctx.editor) return
      const url = field.value.trim()
      if (url) ctx.editor.chain().focus().setLink({ href: url }).run()
      linkInput.style.display = "none"
    }

    apply.addEventListener("mousedown", (e) => {
      e.preventDefault()
      applyLink()
    })

    field.addEventListener("keydown", (e) => {
      if (e.key === "Enter") { e.preventDefault(); applyLink() }
      if (e.key === "Escape") linkInput.style.display = "none"
    })
  }
}

export function bubbleMenuShouldShow({ editor, from, to }) {
  if (window._tiptapAiProcessing) return false
  if (from === to) return false
  // Don't show for node selections (images, etc.)
  if (editor.state.selection.node) return false
  const resolved = editor.state.doc.resolve(from)
  if (resolved.marks().some(m => m.type.name === "aiHighlight")) return false
  return true
}

// ctx: { editor, editorEl, containerEl, syncToField }
export function triggerAiAction(editor, action, ctx) {
  if (ctx.aiDropdownEl) ctx.aiDropdownEl.style.display = "none"
  if (!editor || editor.isDestroyed || window._tiptapAiProcessing) return

  const { from, to } = editor.state.selection
  const text = editor.state.doc.textBetween(from, to)
  if (text.length < 5) return

  if (editor.state.doc.rangeHasMark(from, to, editor.schema.marks.aiHighlight)) {
    showAiNotice(ctx, 'Accept or decline the existing suggestion before editing it again.')
    return
  }

  const request = {
    from, to, action, text, doc: editor.state.doc,
    postId: ctx.editorEl?.closest('.post-form-wrapper')?.getAttribute('data-post-id') ||
      document.querySelector('.post-form-wrapper')?.getAttribute('data-post-id'),
  }
  return requestAiEdit(editor, ctx, request)
}

function requestStillCurrent(editor, ctx, request) {
  return !editor.isDestroyed && ctx.editorEl?.isConnected && editor.state.doc.eq(request.doc)
}

async function requestAiEdit(editor, ctx, request) {
  if (window._tiptapAiProcessing) return
  if (!requestStillCurrent(editor, ctx, request)) {
    showAiNotice(ctx, 'The text changed. Select the passage again to request a new edit.')
    return
  }

  window._tiptapAiProcessing = true
  clearAiNotice(ctx)
  if (window._showAiEditorLoading) window._showAiEditorLoading(request.action)
  const abort = new AbortController()
  const timeout = setTimeout(() => abort.abort(), 60000)
  const onDestroy = () => abort.abort()
  editor.on('destroy', onDestroy)
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ""
  try {
    const response = await fetch('/posts/ai_text_action', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ text: request.text, ai_action: request.action, post_id: request.postId, marker_id: 'ai-stream-' + Date.now() }),
      credentials: 'same-origin', signal: abort.signal,
    })
    if (!response.ok) throw new Error('Request failed')
    const data = await response.json()
    if (data.status !== 'success' || typeof data.content !== 'string' || !data.content.trim() || !data.edit_id) throw new Error('Invalid suggestion')
    if (!requestStillCurrent(editor, ctx, request)) {
      showAiNotice(ctx, 'The text changed while AI was working. Your current writing was kept; select a passage to try again.')
      return
    }
    Object.assign(ctx, { _aiFrom: request.from, _aiTo: request.to, _aiAction: request.action, _aiOriginalText: request.text })
    insertAiResponse(editor, data.content, data.edit_id, ctx)
  } catch (_) {
    if (!editor.isDestroyed && ctx.editorEl?.isConnected) {
      showAiNotice(ctx, 'The AI edit could not be completed. Your text is unchanged.', () => requestAiEdit(editor, ctx, request))
    }
  } finally {
    clearTimeout(timeout)
    editor.off('destroy', onDestroy)
    window._tiptapAiProcessing = false
    if (window._removeAiEditorLoading) window._removeAiEditorLoading()
  }
}

function clearAiNotice(ctx) {
  ctx._aiNotice?.remove()
  ctx._aiNotice = null
}

function showAiNotice(ctx, message, retry) {
  if (!ctx.editorEl?.isConnected) return
  clearAiNotice(ctx)
  const notice = document.createElement('div')
  notice.className = 'ai-edit-notice'
  notice.setAttribute('role', 'status')
  notice.setAttribute('aria-live', 'polite')
  const text = document.createElement('span')
  text.textContent = message
  notice.appendChild(text)
  if (retry) {
    const button = document.createElement('button')
    button.type = 'button'
    button.textContent = 'Retry'
    button.onclick = () => { clearAiNotice(ctx); retry() }
    notice.appendChild(button)
    // Failures can be reported straight from the notice; the feedback panel picks up the message.
    if (document.getElementById('feedback-drawer')) {
      const report = document.createElement('button')
      report.type = 'button'
      report.textContent = 'Report this problem'
      report.dataset.drawerOpen = 'feedback-drawer'
      report.dataset.drawerFill = JSON.stringify({ 'feedback[topic]': 'bug' })
      report.dataset.feedbackError = message
      notice.appendChild(report)
    }
  }
  const dismiss = document.createElement('button')
  dismiss.type = 'button'
  dismiss.textContent = 'Dismiss'
  dismiss.onclick = () => clearAiNotice(ctx)
  notice.appendChild(dismiss)
  ;(ctx.containerEl || ctx.editorEl).prepend(notice)
  ctx._aiNotice = notice
}

function insertAiResponse(editor, htmlContent, editId, ctx) {
  const action = ctx._aiAction
  const originalText = ctx._aiOriginalText
  // The inline endpoint returns plain text; never interpret model output as HTML.
  const cleanText = htmlContent

  editor
    .chain()
    .focus()
    .deleteRange({ from: ctx._aiFrom, to: ctx._aiTo })
    .insertContentAt(ctx._aiFrom, { type: 'text', text: cleanText })
    .run()

  const endPos = Math.min(ctx._aiFrom + cleanText.length, editor.state.doc.content.size)
  editor
    .chain()
    .setTextSelection({ from: ctx._aiFrom, to: endPos })
    .setAiHighlight({
      "data-ai-action": action,
      "data-ai-original": encodeURIComponent(originalText),
      "data-edit-id": editId || null,
      class: "ai-editor-highlight",
    })
    .run()

  if (ctx.syncToField) ctx.syncToField()
  window._tiptapAiProcessing = false

  setTimeout(() => {
    if (editor.isDestroyed) return
    const pmEl = ctx.editorEl?.querySelector(".ProseMirror")
    if (!pmEl) return

    const highlight = Array.from(pmEl.querySelectorAll('mark.ai-editor-highlight')).find(mark => mark.dataset.editId === String(editId))

    if (highlight) {
      showAiControlsFor(editor, highlight, editId, action, originalText, ctx)
    }
  }, 150)
}

export function showAiControlsFor(editor, highlight, editId, action, originalText, ctx) {
  if (!highlight || editor.isDestroyed || highlight.dataset.aiControlsId) return

  const relatedHighlights = () => editId ? Array.from(ctx.editorEl.querySelectorAll('mark.ai-editor-highlight'))
    .filter(mark => mark.dataset.editId === String(editId)) : [highlight]
  const existingControlsId = relatedHighlights().find(mark => mark.dataset.aiControlsId)?.dataset.aiControlsId
  if (existingControlsId) {
    relatedHighlights().forEach(mark => { mark.dataset.aiControlsId = existingControlsId })
    return
  }
  const controlsId = 'ai-ctrl-' + Date.now() + '-' + Math.random().toString(36).slice(2)
  relatedHighlights().forEach(mark => { mark.dataset.aiControlsId = controlsId })
  const controls = document.createElement('div')
  controls.className = 'ai-edit-inline-controls fade-in'
  controls.dataset.controlsId = controlsId
  const labels = { rewrite: 'Rewritten', expand: 'Expanded', simplify: 'Simplified', summarize: 'Summarized' }
  controls.innerHTML = `
    <span class="ai-edit-inline-label">${labels[action] || 'AI Edit'}</span>
    <div class="ai-edit-inline-buttons">
      <button type="button" class="ai-edit-inline-btn ai-action-accept" title="Accept" aria-label="Accept edit"><span class="fas fa-check"></span></button>
      <button type="button" class="ai-edit-inline-btn ai-action-decline" title="Decline" aria-label="Decline edit"><span class="fas fa-times"></span></button>
      <button type="button" class="ai-edit-inline-btn ai-action-regenerate" title="Regenerate" aria-label="Regenerate edit"><span class="fas fa-arrows-rotate"></span></button>
    </div>`

  const container = ctx.containerEl || ctx.editorEl
  let saving = false
  const cleanup = () => {
    controls.remove()
    relatedHighlights().forEach(mark => { delete mark.dataset.aiControlsId })
    container.removeEventListener('scroll', positionControls)
    window.removeEventListener('resize', positionControls)
    editor.off('destroy', cleanup)
  }
  const positionControls = () => {
    if (!highlight.isConnected && editId) highlight = relatedHighlights()[0] || highlight
    if (!highlight.isConnected) { cleanup(); return }
    const hlRect = highlight.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()
    controls.style.position = 'absolute'
    controls.style.top = (hlRect.top + hlRect.height / 2 - containerRect.top + container.scrollTop - (controls.offsetHeight || 60) / 2) + 'px'
    controls.style.right = '8px'
  }
  container.style.position = 'relative'
  container.appendChild(controls)
  positionControls()
  container.addEventListener('scroll', positionControls)
  window.addEventListener('resize', positionControls)
  editor.on('destroy', cleanup)
  controls.addEventListener('mouseenter', () => highlight.classList.add('ai-highlight-hover'))
  controls.addEventListener('mouseleave', () => highlight.classList.remove('ai-highlight-hover'))

  const findRange = () => {
    if (editor.isDestroyed) return null
    if (!editId) {
      // Legacy highlights have no record ID. Never fall back to another mark.
      if (!highlight.isConnected) return null
      const from = editor.view.posAtDOM(highlight, 0)
      return { from, to: from + highlight.textContent.length }
    }
    let range = null
    let split = false
    editor.state.doc.descendants((node, pos) => {
      if (!node.isText) return
      const mark = node.marks.find(m => m.type.name === 'aiHighlight' && String(m.attrs['data-edit-id']) === String(editId))
      if (!mark) return
      if (range && range.to !== pos) split = true
      range = { from: range ? range.from : pos, to: pos + node.nodeSize }
    })
    return split ? null : range
  }

  const clearSavedHighlights = () => {
    if (!editId) return
    const transaction = editor.state.tr
    editor.state.doc.descendants((node, pos) => {
      const mark = node.marks.find(m => m.type.name === 'aiHighlight' && String(m.attrs['data-edit-id']) === String(editId))
      if (node.isText && mark) transaction.removeMark(pos, pos + node.nodeSize, mark)
    })
    editor.view.dispatch(transaction)
  }

  const decide = async (decision, regenerate = false) => {
    if (saving || editor.isDestroyed) return
    const range = findRange()
    if (!range) { showAiNotice(ctx, 'This suggestion was removed, split, or copied. Undo that change before reviewing it.'); return }
    const snapshot = editor.state.doc.slice(range.from, range.to)
    saving = true
    controls.querySelectorAll('button').forEach(button => { button.disabled = true })
    clearAiNotice(ctx)
    try {
      const result = editId ? await updateAiEdit(editId, decision, editor, regenerate) : { decision }
      if (editor.isDestroyed || !ctx.editorEl?.isConnected) return
      const currentRange = findRange()
      const unchanged = currentRange && editor.state.doc.slice(currentRange.from, currentRange.to).eq(snapshot) &&
        (typeof result.edited_text !== 'string' || editor.state.doc.textBetween(currentRange.from, currentRange.to) === result.edited_text)
      const savedOriginal = typeof result.original_text === 'string' ? result.original_text : originalText
      if (result.decision === 'declined' && unchanged) {
        editor.chain().focus().setTextSelection(currentRange).unsetAiHighlight().deleteRange(currentRange)
          .insertContentAt(currentRange.from, savedOriginal ? { type: 'text', text: savedOriginal } : []).run()
      } else {
        if (editId) clearSavedHighlights()
        else if (currentRange) editor.chain().setTextSelection(currentRange).unsetAiHighlight().run()
        if (result.decision === 'declined') showAiNotice(ctx, 'The decline was saved. Your newer writing was kept.')
      }
      const anchor = { container, top: controls.style.top }
      cleanup()
      if (ctx.syncToField) ctx.syncToField()
      if (result.feedback && !regenerate) showDeclineReasons(anchor, result.feedback)
      if (result.decision !== decision) showAiNotice(ctx, `This edit was already ${result.decision}. The saved decision was applied; your other writing was kept.`)
      if (regenerate && result.decision === decision && unchanged) {
        editor.commands.setTextSelection({ from: currentRange.from, to: currentRange.from + savedOriginal.length })
        await triggerAiAction(editor, action, ctx)
      }
    } catch (_) {
      if (!editor.isDestroyed && ctx.editorEl?.isConnected) {
        showAiNotice(ctx, 'Your decision could not be saved. The suggestion is still available to review.', () => decide(decision, regenerate))
      }
    } finally {
      saving = false
      controls.querySelectorAll('button').forEach(button => { button.disabled = false })
    }
  }
  for (const [selector, decision, regenerate] of [
    ['.ai-action-accept', 'accepted', false],
    ['.ai-action-decline', 'declined', false],
    ['.ai-action-regenerate', 'declined', true],
  ]) {
    controls.querySelector(selector).onclick = event => {
      event.preventDefault()
      event.stopPropagation()
      decide(decision, regenerate)
    }
  }
}

export function restorePersistedHighlights(editor, editorEl, ctx) {
  const pmEl = editorEl?.querySelector(".ProseMirror")
  if (!pmEl) return

  const marks = pmEl.querySelectorAll("mark.ai-editor-highlight")
  marks.forEach(mark => {
    const action = mark.dataset.aiAction || "rewrite"
    const original = mark.dataset.aiOriginal ? decodeURIComponent(mark.dataset.aiOriginal) : mark.textContent
    const editId = mark.dataset.editId
    showAiControlsFor(editor, mark, editId, action, original, ctx)
  })
}

export function highlightActiveAiControls(editorEl) {
  document.querySelectorAll(".ai-edit-inline-controls").forEach(c => {
    c.classList.remove("ai-controls-active")
  })

  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return

  let node = sel.anchorNode
  while (node && node !== editorEl) {
    if (node.nodeType === 1 && node.classList && node.classList.contains("ai-editor-highlight")) {
      const linkedId = node.dataset.aiControlsId
      if (linkedId) {
        const controls = document.querySelector(`.ai-edit-inline-controls[data-controls-id="${linkedId}"]`)
        if (controls) controls.classList.add("ai-controls-active")
      }
      break
    }
    node = node.parentNode
  }
}

async function updateAiEdit(editId, status, editor, regenerate = false) {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ''
  const abort = new AbortController()
  const timeout = setTimeout(() => abort.abort(), 60000)
  const onDestroy = () => abort.abort()
  editor.on('destroy', onDestroy)
  try {
    const response = await fetch('/posts/ai_edit_update', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ edit_id: editId, status, regenerate }),
      credentials: 'same-origin', signal: abort.signal,
    })
    const result = await response.json()
    const acknowledged = response.ok && result.status === 'success' && result.decision === status
    const alreadyDecided = response.status === 409 && ['accepted', 'declined'].includes(result.decision)
    if (result.edit_id !== editId || !(acknowledged || alreadyDecided)) throw new Error('Decision was not acknowledged')
    return result
  } finally {
    clearTimeout(timeout)
    editor.off('destroy', onDestroy)
  }
}

// A small, optional "What was off?" row after a Decline. It never takes focus
// from the editor and goes away on its own if ignored.
function showDeclineReasons(anchor, feedback) {
  const bar = document.createElement('div')
  bar.className = 'ai-edit-reasons fade-in'
  bar.setAttribute('role', 'group')
  bar.setAttribute('aria-label', feedback.prompt)
  bar.style.position = 'absolute'
  bar.style.top = anchor.top || '0px'
  bar.style.right = '8px'

  const head = document.createElement('div')
  head.className = 'ai-edit-reasons-head'
  const label = document.createElement('span')
  label.className = 'ai-edit-reasons-label'
  label.textContent = feedback.prompt
  const close = document.createElement('button')
  close.type = 'button'
  close.className = 'ai-edit-reasons-close'
  close.setAttribute('aria-label', feedback.dismiss)
  close.innerHTML = '<span class="fas fa-times" aria-hidden="true"></span>'
  close.addEventListener('click', event => { event.preventDefault(); bar.remove() })
  head.append(label, close)

  const list = document.createElement('div')
  list.className = 'ai-edit-reasons-list'
  ;(feedback.reasons || []).forEach(({ key, label: text }) => {
    const chip = document.createElement('button')
    chip.type = 'button'
    chip.className = 'ai-edit-reason'
    chip.textContent = text
    chip.addEventListener('click', event => {
      event.preventDefault()
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ''
      fetch(feedback.url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ reasons: [key] }),
        credentials: 'same-origin',
      }).catch(() => {})
      const thanks = document.createElement('span')
      thanks.className = 'ai-edit-reasons-thanks'
      thanks.setAttribute('role', 'status')
      thanks.textContent = feedback.thanks
      bar.replaceChildren(thanks)
      setTimeout(() => bar.remove(), 2400)
    })
    list.appendChild(chip)
  })

  bar.append(head, list)
  anchor.container.appendChild(bar)
  setTimeout(() => { if (bar.isConnected && !bar.matches(':hover')) bar.remove() }, 15000)
}
